import React from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useFetch } from 'src/hooks/useFetch';
import { Modal } from '..';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import {
  ITOP_TICKET_IMPACT_MAP,
  ITOP_TICKET_URGENCY_MAP,
} from '@siptel-web/common';

const validationSchema = yup.object({
  id_contacto: yup
    .number()
    .positive()
    .integer()
    .typeError('Debe seleccionar un usuario')
    .required('Debe seleccionar un usuario'),
  asunto: yup.string().trim().required('Debe ingresar un asunto'),
  id_servicio: yup
    .number()
    .positive()
    .integer()
    .typeError('Debe seleccionar un servicio')
    .required('Debe seleccionar un servicio'),
  id_subcategoria_servicio: yup
    .number()
    .positive()
    .integer()
    .typeError('Debe seleccionar una subcategoria')
    .required('Debe seleccionar una subcategoria'),
  urgencia: yup
    .number()
    .oneOf(Object.keys(ITOP_TICKET_URGENCY_MAP).map(Number))
    .typeError('Debe seleccionar una urgencia')
    .required('Debe seleccionar una urgencia'),
  impacto: yup
    .number()
    .oneOf(Object.keys(ITOP_TICKET_IMPACT_MAP).map(Number))
    .typeError('Debe seleccionar un impacto')
    .required('Debe seleccionar un impacto'),
  descripcion: yup.string().trim().required('Debe ingresar una descripción'),
});

function CrearTicketModal(props) {
  const { open, close, afterConfirm } = props;

  const isRequerimiento = props?.data?.type === 'R';

  const {
    fetch: getContactsRequest,
    loading: loadingContacts,
    data: contacts,
  } = useFetch();
  const {
    fetch: getServicesRequest,
    loading: loadingServices,
    data: services,
  } = useFetch();

  const {
    fetch: getServiceSubCategoriesRequest,
    loading: loadingServiceSubCategories,
    data: serviceSubCategories,
  } = useFetch();

  const { fetch: createTicketRequest, loading: creatingTicket } = useFetch();

  const onCloseModal = React.useCallback(() => {
    close();
  }, [close]);

  const getContacts = React.useCallback(() => {
    getContactsRequest(`/tickets/contactos`);
  }, [getContactsRequest]);

  const getServices = React.useCallback(() => {
    getServicesRequest(`/tickets/servicios`);
  }, [getServicesRequest]);

  const getServiceSubCategories = React.useCallback(
    (id_servicio) => {
      getServiceSubCategoriesRequest(
        `/tickets/servicios/${id_servicio}/subcategorias`,
      );
    },
    [getServiceSubCategoriesRequest],
  );

  const createTicket = React.useCallback(
    async (data) => {
      await createTicketRequest(
        `/tickets/${isRequerimiento ? 'requerimientos' : 'incidentes'}`,
        {
          method: 'POST',
          data,
        },
      );
    },
    [createTicketRequest, isRequerimiento],
  );

  React.useEffect(() => {
    getContacts();
    getServices();
  }, [getContacts, getServices]);

  const handleSelectChange = (formik, field, event) => {
    const {
      target: { value },
    } = event;
    formik.setFieldValue(field, value);

    if (field === 'id_servicio') {
      getServiceSubCategories(value);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id_contacto: '',
      asunto: '',
      id_servicio: '',
      id_subcategoria_servicio: '',
      impacto: '',
      urgencia: '',
      descripcion: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await createTicket(values);
      resetForm();
      close();

      if (afterConfirm && typeof afterConfirm === 'function') {
        afterConfirm();
      }
    },
  });

  return (
    <Modal
      title={`Crear ${isRequerimiento ? 'Requerimiento' : 'Incidente'}`}
      maxWidth="md"
      content={
        <>
          <Grid container spacing={4}>
            <Grid item md={6} sm={6}>
              <FormControl
                sx={{ mt: 3, display: 'flex' }}
                error={
                  formik.touched.id_contacto &&
                  Boolean(formik.errors.id_contacto)
                }
                required
                disabled={loadingContacts}
              >
                <InputLabel id="contacto-select-label">
                  Reportado Por
                </InputLabel>
                <Select
                  labelId="contacto-select-label"
                  id="contacto-select"
                  value={formik.values.id_contacto}
                  label="Reportado Por"
                  onChange={handleSelectChange.bind(
                    null,
                    formik,
                    'id_contacto',
                  )}
                >
                  {(contacts ?? []).map((contact) => (
                    <MenuItem key={contact.id} value={contact.id}>
                      {contact.nombre}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.id_contacto && formik.errors.id_contacto && (
                  <FormHelperText>{formik.errors.id_contacto}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item md={6} sm={6}>
              <TextField
                margin="normal"
                sx={{ mt: 3, mb: 0 }}
                required
                fullWidth
                id="asunto"
                label="Asunto"
                name="asunto"
                autoComplete="asunto"
                value={formik.values.asunto}
                onChange={formik.handleChange}
                error={formik.touched.asunto && Boolean(formik.errors.asunto)}
                helperText={formik.touched.asunto && formik.errors.asunto}
              />
            </Grid>
          </Grid>
          <Grid container spacing={4}>
            <Grid item md={6} sm={6}>
              <FormControl
                sx={{ mt: 3, display: 'flex' }}
                error={
                  formik.touched.id_servicio &&
                  Boolean(formik.errors.id_servicio)
                }
                required
                disabled={loadingServices}
              >
                <InputLabel id="servicio-select-label">Servicio</InputLabel>
                <Select
                  labelId="servicio-select-label"
                  id="servicio-select"
                  value={formik.values.id_servicio}
                  label="Servicio"
                  onChange={handleSelectChange.bind(
                    null,
                    formik,
                    'id_servicio',
                  )}
                >
                  {(services ?? []).map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.nombre}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.id_servicio && formik.errors.id_servicio && (
                  <FormHelperText>{formik.errors.id_servicio}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item md={6} sm={6}>
              <FormControl
                sx={{ mt: 3, display: 'flex' }}
                error={
                  formik.touched.id_subcategoria_servicio &&
                  Boolean(formik.errors.id_subcategoria_servicio)
                }
                required
                disabled={
                  !formik.values.id_servicio || loadingServiceSubCategories
                }
              >
                <InputLabel id="subcategoria-select-label">
                  Subcategoría
                </InputLabel>
                <Select
                  labelId="subcategoria-select-label"
                  id="subcategoria-select"
                  value={formik.values.id_subcategoria_servicio}
                  label="Subcategoría"
                  onChange={handleSelectChange.bind(
                    null,
                    formik,
                    'id_subcategoria_servicio',
                  )}
                >
                  {(serviceSubCategories ?? []).map((subcategory) => (
                    <MenuItem key={subcategory.id} value={subcategory.id}>
                      {subcategory.nombre}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.id_subcategoria_servicio &&
                  formik.errors.id_subcategoria_servicio && (
                    <FormHelperText>
                      {formik.errors.id_subcategoria_servicio}
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={4}>
            <Grid item md={6} sm={6}>
              <FormControl
                sx={{ mt: 3, display: 'flex' }}
                error={formik.touched.impacto && Boolean(formik.errors.impacto)}
                required
              >
                <InputLabel id="impacto-select-label">Impacto</InputLabel>
                <Select
                  labelId="impacto-select-label"
                  id="impacto-select"
                  value={formik.values.impacto}
                  label="Impacto"
                  onChange={handleSelectChange.bind(null, formik, 'impacto')}
                >
                  {Object.keys(ITOP_TICKET_IMPACT_MAP).map((value) => (
                    <MenuItem key={`ticket-impact-${value}`} value={+value}>
                      {ITOP_TICKET_IMPACT_MAP[value]}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.impacto && formik.errors.impacto && (
                  <FormHelperText>{formik.errors.impacto}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item md={6} sm={6}>
              <FormControl
                sx={{ mt: 3, display: 'flex' }}
                error={
                  formik.touched.urgencia && Boolean(formik.errors.urgencia)
                }
                required
              >
                <InputLabel id="urgencia-select-label">Urgencia</InputLabel>
                <Select
                  labelId="urgencia-select-label"
                  id="urgencia-select"
                  value={formik.values.urgencia}
                  label="Urgencia"
                  onChange={handleSelectChange.bind(null, formik, 'urgencia')}
                >
                  {Object.keys(ITOP_TICKET_URGENCY_MAP).map((value) => (
                    <MenuItem key={`ticket-urgency-${value}`} value={+value}>
                      {ITOP_TICKET_URGENCY_MAP[value]}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.urgencia && formik.errors.urgencia && (
                  <FormHelperText>{formik.errors.urgencia}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            rows={3}
            id="descripcion"
            label="Descripción"
            name="descripcion"
            autoComplete="descripcion"
            value={formik.values.descripcion}
            onChange={formik.handleChange}
            error={
              formik.touched.descripcion && Boolean(formik.errors.descripcion)
            }
            helperText={formik.touched.descripcion && formik.errors.descripcion}
          />
        </>
      }
      open={open}
      close={onCloseModal}
      onConfirm={formik.handleSubmit}
      disabled={creatingTicket}
      isConfirming={creatingTicket}
      confirmButtonText="Guardar"
      cancelButtonText="Cerrar"
    />
  );
}

export default CrearTicketModal;
